import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import type { User, AuthResponse } from '../services/api';
import {
  getStoredAuth,
  setStoredAuth,
  clearAuth as clearAuthStorage,
} from '../services/authStorage.js';
import { initializeOnboardingStateFromBackend } from '../constants/onboardingFlow';

interface AuthContextType {
  user: User | null;
  tokens: { access: string; refresh: string } | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasCompletedAssessment: boolean;
  login: (authResponse: AuthResponse) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication Context
 *
 * Manages user authentication state for the app. Features:
 * - Session persistence via localStorage
 * - Token refresh via fetchWrapper events
 * - Login/logout methods for components
 * - Memoized functions to prevent unnecessary re-renders
 *
 * Note: Auto guest login is NOT handled here. It is handled by:
 * - AuthLandingPage: "Continue as Guest" button for explicit login
 * - ActivityDetailsPage: Auto guest login for shared activity links
 */

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<{ access: string; refresh: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasCompletedAssessment, setHasCompletedAssessment] = useState<boolean>(false);


    // useEffect(() => {
      
    //   const getToken = async () => {
    //     try {
    //       const token = await refreshAccessTokenTest();
    //       console.log('Refreshed token:', token);
    //       return token;
    //     } catch (error) {
    //       throw error;
    //     }

    //   }

    //  // getToken();

    // }, []);

  // Load auth from localStorage on mount
  useEffect(() => {
    try {
      const storedAuth = getStoredAuth();
      if (storedAuth) {
        setUser(storedAuth.user);
        setTokens(storedAuth.tokens);
        // Default false for old sessions that predate this field
        setHasCompletedAssessment(storedAuth.hasCompletedAssessment ?? false);
      }
    } catch (error) {
      console.error('Failed to load auth from storage:', error);
      // Clear invalid data
      clearAuthStorage();
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize onboarding state from backend after auth is loaded
  useEffect(() => {
    if (!isLoading && user) {
      // User is authenticated - check if they have children and set flag accordingly
      initializeOnboardingStateFromBackend().catch((error) => {
        console.error('[AuthContext] Failed to initialize onboarding state:', error);
        // Non-critical error - don't block app, user can still navigate manually
      });
    }
  }, [isLoading, user]);

  // Listen for token updates from fetchWrapper
  useEffect(() => {
    const handleTokenUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ tokens: { access: string; refresh: string } }>;
      if (customEvent.detail?.tokens) {
        console.log('[AuthContext] Token(s) updated by fetchWrapper, updating state');
        setTokens(customEvent.detail.tokens);
      }
    };

    const handleAuthCleared = () => {
      console.log('[AuthContext] Auth cleared by fetchWrapper, logging out');
      setUser(null);
      setTokens(null);
    };

    window.addEventListener('auth-token-updated', handleTokenUpdate);
    window.addEventListener('auth-cleared', handleAuthCleared);

    return () => {
      window.removeEventListener('auth-token-updated', handleTokenUpdate);
      window.removeEventListener('auth-cleared', handleAuthCleared);
    };
  }, []);

  // Memoized to maintain stable reference across renders
  // Prevents unnecessary re-renders in components that depend on this function
  const login = useCallback((authResponse: AuthResponse) => {
    console.log('[AuthContext] Login successful:', authResponse.user.nickname);
    setUser(authResponse.user);
    setTokens(authResponse.tokens);
    setHasCompletedAssessment(authResponse.hasCompletedAssessment);
    setStoredAuth({
      user: authResponse.user,
      tokens: authResponse.tokens,
      hasCompletedAssessment: authResponse.hasCompletedAssessment,
    });
  }, []); // Empty deps - only updates state

  const logout = useCallback(() => {
    console.log('[AuthContext] Logging out');
    setUser(null);
    setTokens(null);
    setHasCompletedAssessment(false);
    clearAuthStorage();
  }, []); // Empty deps - only updates state


  const value: AuthContextType = {
    user,
    tokens,
    isAuthenticated: !!user && !!tokens,
    isLoading,
    hasCompletedAssessment,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}


// async function refreshAccessTokenTest(): Promise<string> {
//   const refreshToken = getRefreshToken();
  
//   if (!refreshToken) {
//     throw new Error('No refresh token available');
//   }

//   // Get API URL from environment
//   const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

//   console.log('[fetchWrapper] Calling refresh endpoint...');
  
//   // Call the refresh endpoint (will be implemented in backend later)
//   const response = await fetch(`${API_URL}/auth/refresh`, {
//     method: 'POST',
//     headers: { 
//       'Content-Type': 'application/json',
//     },
//     body: JSON.stringify({ refreshToken }),
//   });

//   if (!response.ok) {
//     // Get error message from response if available
//     let errorMessage = 'Token refresh failed';
//     try {
//       const errorData = await response.json();
//       errorMessage = errorData.error || errorData.message || errorMessage;
//     } catch {
//       // Ignore JSON parse errors
//     }
    
//     throw new Error(errorMessage);
//   }

//   const result = await response.json();

//   console.log('[fetchWrapper] Refresh response received:', result);
  
//   // Extract tokens from response
//   // Backend should return: { success: true, data: { accessToken: "...", refreshToken: "..." } }
//   const newAccessToken = result.data?.tokens.access 
//   const newRefreshToken = result.data?.tokens.refresh 
  
//   console.log('[fetchWrapper] Token(s) updated successfully', newRefreshToken);
  
//   return newAccessToken;
// }

